/**
 * Plan gating hook for the frontend.
 *
 * Reads plan_info from the auth session and provides helpers to check
 * feature availability and resource limits. In dev mode (no auth),
 * everything is unlocked (enterprise tier).
 */

import { useMemo } from 'react';
import { useAuth } from '../auth/AuthProvider';

export type PlanInfo = {
  plan: string;
  max_members: number | null;
  max_projects: number | null;
  max_connectors: number | null;
  monthly_token_limit: number | null;
  audit_retention_days: number;
  limit_enforcement: 'hard' | 'soft';
  features: Record<string, boolean>;
};

const ENTERPRISE_DEFAULTS: PlanInfo = {
  plan: 'enterprise',
  max_members: null,
  max_projects: null,
  max_connectors: null,
  monthly_token_limit: null,
  audit_retention_days: 365,
  limit_enforcement: 'soft',
  features: {
    messaging: true,
    dm: true,
    api_keys: true,
    keycloak: true,
    custom_policies: true,
    org_admin_controls: true,
    invite_members: true,
  },
};

export const usePlanGate = () => {
  const { user } = useAuth();

  const planInfo: PlanInfo = useMemo(() => {
    const raw = (user as Record<string, unknown> | null)?.plan_info;
    if (raw && typeof raw === 'object') {
      return raw as PlanInfo;
    }
    // Default to enterprise in dev / no-auth mode
    return ENTERPRISE_DEFAULTS;
  }, [user]);

  const hasFeature = (feature: string): boolean =>
    planInfo.features[feature] ?? false;

  const isUnlimited = (resource: 'members' | 'projects' | 'connectors'): boolean => {
    const key = `max_${resource}` as keyof PlanInfo;
    return planInfo[key] === null;
  };

  const getLimit = (resource: 'members' | 'projects' | 'connectors'): number | null => {
    const key = `max_${resource}` as keyof PlanInfo;
    return planInfo[key] as number | null;
  };

  return {
    planInfo,
    plan: planInfo.plan,
    hasFeature,
    isUnlimited,
    getLimit,
    isHardLimit: planInfo.limit_enforcement === 'hard',
    isSoftLimit: planInfo.limit_enforcement === 'soft',
  };
};
